import { API_URL, graphql } from './api';

/**
 * Instagram Login in a popup window (decided 2026-10-06, like Instafeed): the merchant stays in the Shopify admin,
 * logs in and allows in the popup, and the app updates in place. Instagram's pages can cut the popup off from this
 * window, so the result comes from the server: the callback records it under the attempt ID and we poll for it.
 * If the browser blocks the popup, the whole admin goes to Instagram and comes back, as before.
 */

const START = `mutation Start($switchAccount: Boolean!) { startInstagramConnect(switchAccount: $switchAccount) { url attempt } }`;
const STATUS = `query Status($attempt: String!) { instagramConnectStatus(attempt: $attempt) }`;
const URL_ONLY = `mutation Connect($switchAccount: Boolean!) { instagramConnectUrl(switchAccount: $switchAccount) }`;
const POLL_MS = 1500;
const TIMEOUT_MS = 5 * 60_000;
const POPUP = { width: 600, height: 760 };

/** Fired on window when a popup connect finishes; App shows the toast and reloads the pages. */
export const CONNECT_EVENT = 'unboxugc:instagram';
export type ConnectResult = 'connected' | 'error' | 'redirected' | 'timeout';
export type ConnectEventDetail = { result: 'connected' | 'error'; reason: string };

function openPopup(): Window | null {
  const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - POPUP.width) / 2));
  const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - POPUP.height) / 2));
  const features = `popup,width=${POPUP.width},height=${POPUP.height},left=${left},top=${top}`;
  return window.open('about:blank', 'unboxugc-instagram', features);
}

async function redirect(switchAccount: boolean): Promise<ConnectResult> {
  const { instagramConnectUrl } = await graphql<{ instagramConnectUrl: string }>(URL_ONLY, { switchAccount });
  window.open(instagramConnectUrl, '_top');
  return 'redirected';
}

function waitForResult(attempt: string): Promise<{ status: string }> {
  const apiOrigin = new URL(API_URL).origin;
  return new Promise(resolve => {
    let done = false;
    const finish = (status: string) => {
      if (done) return;
      done = true;
      clearInterval(timer);
      clearTimeout(deadline);
      window.removeEventListener('message', onMessage);
      resolve({ status });
    };
    const check = async () => {
      try {
        const { instagramConnectStatus } = await graphql<{ instagramConnectStatus: string }>(STATUS, { attempt });
        if (instagramConnectStatus !== 'pending') finish(instagramConnectStatus);
      } catch {
        // a failed poll is retried on the next tick
      }
    };
    // Fast path when the popup can still reach us; the poll covers the rest.
    const onMessage = (event: MessageEvent) => {
      if (event.origin === apiOrigin && event.data?.type === CONNECT_EVENT) void check();
    };
    window.addEventListener('message', onMessage);
    const timer = setInterval(() => void check(), POLL_MS);
    const deadline = setTimeout(() => finish('timeout'), TIMEOUT_MS);
  });
}

/** Must be called straight from a click: the popup opens before anything is awaited, or the browser blocks it. */
export async function startConnect({ switchAccount = false }: { switchAccount?: boolean } = {}): Promise<ConnectResult> {
  const popup = openPopup();
  if (!popup) return redirect(switchAccount);
  let attempt: string;
  try {
    const { startInstagramConnect } = await graphql<{ startInstagramConnect: { url: string; attempt: string } }>(START, {
      switchAccount,
    });
    popup.location.href = startInstagramConnect.url;
    attempt = startInstagramConnect.attempt;
  } catch (error) {
    popup.close();
    throw error;
  }
  const { status } = await waitForResult(attempt);
  if (status === 'timeout') return 'timeout';
  const result = status === 'connected' ? 'connected' : 'error';
  const detail: ConnectEventDetail = { result, reason: status.startsWith('error:') ? status.slice(6) : '' };
  window.dispatchEvent(new CustomEvent<ConnectEventDetail>(CONNECT_EVENT, { detail }));
  return result;
}
